import type {
  GameModesQueryParams,
  SortGameModesColumn
} from '@library/types/gameModes';
import { useEffect, useState } from 'react';
import { DEFAULT_GAME_MODE_DATA } from '@bo-constants/gameModes';
import { DEFAULT_GAME_MODE_SORT } from '@library/constants/sorting';
import { DateTime } from 'luxon';
import { GameModeDetailsModal } from '@bo-components/gameModes/GameModeDetailsModal';
import { GameModesTable } from '@bo-components/gameModes/GameModesTable';
import { Loading } from '@library/components/Loading';
import UtilDashboardHeader from '@bo-components/common/UtilDashboardHeader';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { useGameModesDash } from '@bo-hooks/utilsDashboards/useGameModesDash';
import { usePerPage } from '@bo-hooks/usePerPage';
import useUtilDashboard from '@bo-hooks/utilsDashboards/useUtilDashboard';

export const GameModesDashboard = () => {
  const {
    durations,
    handleSearch,
    handleSelectLeague,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue
  } = useDashBoard({
    initialStartDate: DateTime.fromISO('2018-01-01'),
    dashboardName: 'GAME_MODES'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<SortGameModesColumn>(DEFAULT_GAME_MODE_SORT);
  const [submitMessage, setSubmitMessage] = useState('');
  const [currentItemId, setCurrentItemId] = useState('');

  const { handleSelectPerPage, selectedPerPage } = usePerPage();

  const { id: leagueId } = leagueInfo ?? {};
  const gameModesQueryParams = {
    leagueId,
    page: currentPage,
    perPage: selectedPerPage,
    searchValue,
    sortBy: sort.sortBy,
    sortDir: sort.sortDirection
  } as GameModesQueryParams;

  const {
    isError,
    isLoading,
    gameModes,
    totalPages,
    createGameMode,
    updateGameMode,
    gameTypeOptions,
    allowedStatesOptions,
    relationships
  } = useGameModesDash({
    gameModesQueryParams,
    leaguesList,
    currentItemId,
    setSubmitMessage
  });

  const {
    allItemsChecked,
    currentItemData,
    isDetailsModalOpen,
    idsForCSV,
    isGeneratingCSV,
    isNewItem,
    handleClickAllItemsCheckbox,
    handleClickCheckbox,
    handleCSVExportClick,
    handleAddNewItemClick,
    handleCloseModal,
    handleClickRow
  } = useUtilDashboard({
    data: gameModes,
    path: 'game_modes',
    defaultItemData: DEFAULT_GAME_MODE_DATA,
    setSubmitMessage
  });

  useEffect(() => {
    if (currentItemData.id) {
      setCurrentItemId(currentItemData.id);
    }
  }, [currentItemData.id]);

  if (isLeagueLoading || isLoading) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  return (
    <div className="h-full w-auto">
      <UtilDashboardHeader
        isCSVExportDisabled={idsForCSV.length === 0}
        isGeneratingCSV={isGeneratingCSV}
        handleCSVExportClick={handleCSVExportClick}
        handleCreateNewItemClick={handleAddNewItemClick}
        pageTitle="Game Modes"
        isCreateAllowed={false}
      />

      <GameModesTable
        gameModes={gameModes}
        totalPages={totalPages}
        handleSelectPerPage={handleSelectPerPage}
        selectedPerPage={selectedPerPage}
        durations={durations}
        handleSearch={handleSearch}
        handleSelectLeague={handleSelectLeague}
        searchValue={searchValue}
        leaguesList={leaguesList}
        selectedLeagueId={leagueId}
        isError={isError}
        allItemsChecked={allItemsChecked}
        handleClickAllItemsCheckbox={handleClickAllItemsCheckbox}
        handleClickCheckbox={handleClickCheckbox}
        handleClickRow={handleClickRow}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        sort={sort}
        setSort={setSort}
        relationships={relationships}
      />

      <GameModeDetailsModal
        isNewItem={isNewItem}
        isOpen={isDetailsModalOpen}
        onClose={handleCloseModal}
        onSubmit={isNewItem ? createGameMode : updateGameMode}
        modalData={currentItemData}
        submitMessage={submitMessage}
        gameTypeOptions={gameTypeOptions}
        allowedStatesOptions={allowedStatesOptions}
      />
    </div>
  );
};
